// Slide-out "Trade preview" panel + the context that opens it.
//
// Clicking a trade row (flat grid or grouped session table) opens this
// right-anchored Dialog with the trade's header, badges, and the shared
// TradeStatRail, plus a "Go to trade details" link into the full page.
// The panel is mounted once by TradePreviewProvider; rows trigger it via
// useTradePreview(). DaySessionsTable links degrade to a router Link when
// no provider is mounted (TradeDetailLink), so it stays usable in tests
// and any future host that has not wrapped it in the provider.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { useAccounts } from "../../api/hooks/useAccounts";
import { useInstruments } from "../../api/hooks/useInstruments";
import { useTrade } from "../../api/hooks/useTrade";
import type { components } from "../../api/schema";
import { formatDate, formatDateTime, formatTime } from "../../lib/date";
import { formatMoney, formatPrice, formatSignedMoney, sumDecimals } from "../../lib/money";
import {
  FILL_ACTION_EXECUTION_LABELS,
  formatHoldDuration,
  formatPercent,
  formatQuantity,
} from "../../lib/tradeFormat";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { TradeNoteEditor } from "./TradeNoteEditor";
import { DirectionBadge, OutcomeBadge, StatusBadge, SymbolAvatar } from "./tradeCells";
import { pnlClassName } from "./tradeColumns";

type TradeResponse = components["schemas"]["TradeResponse"];
type FillResponse = components["schemas"]["FillResponse"];

const EM_DASH = "—";

type TradePreviewContextValue = {
  openTradeId: string | null;
  open: (tradeId: string) => void;
  close: () => void;
};

const TradePreviewContext = createContext<TradePreviewContextValue | null>(null);

export function TradePreviewProvider({ children }: { children: ReactNode }) {
  const [openTradeId, setOpenTradeId] = useState<string | null>(null);
  const open = useCallback((tradeId: string) => setOpenTradeId(tradeId), []);
  const close = useCallback(() => setOpenTradeId(null), []);
  const value = useMemo<TradePreviewContextValue>(
    () => ({ openTradeId, open, close }),
    [openTradeId, open, close],
  );

  return (
    <TradePreviewContext.Provider value={value}>
      {children}
      <Dialog open={openTradeId !== null} onOpenChange={(next) => (next ? undefined : close())}>
        <DialogContent
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 left-auto top-0 bottom-0 grid h-full max-h-screen w-full max-w-md translate-x-0 translate-y-0 grid-rows-[auto_1fr_auto] gap-0 overflow-hidden rounded-none border-l p-0 sm:rounded-none"
        >
          {openTradeId !== null ? (
            <TradePreviewBody tradeId={openTradeId} onClose={close} />
          ) : null}
        </DialogContent>
      </Dialog>
    </TradePreviewContext.Provider>
  );
}

export function useTradePreview(): TradePreviewContextValue {
  const ctx = useContext(TradePreviewContext);
  if (ctx === null) {
    throw new Error("useTradePreview must be used inside <TradePreviewProvider>");
  }
  return ctx;
}

// The preview context if a provider is mounted, otherwise null. Lets a row make
// its whole surface open the preview without requiring the provider (callers
// fall back to the per-cell TradeDetailLink, which degrades to a router Link).
export function useOptionalTradePreview(): TradePreviewContextValue | null {
  return useContext(TradePreviewContext);
}

// A trade link that opens the preview panel when a provider is mounted, and
// otherwise navigates to the full detail page. Lets DaySessionsTable stay
// decoupled from the provider (and from prop-drilling an open handler).
export function TradeDetailLink({
  tradeId,
  className,
  stopPropagation = false,
  children,
}: {
  tradeId: string;
  className?: string;
  stopPropagation?: boolean;
  children: ReactNode;
}) {
  const ctx = useContext(TradePreviewContext);
  if (ctx !== null) {
    return (
      <button
        type="button"
        className={className}
        onClick={(event) => {
          if (stopPropagation) event.stopPropagation();
          ctx.open(tradeId);
        }}
      >
        {children}
      </button>
    );
  }
  return (
    <Link
      to="/trades/$tradeId"
      params={{ tradeId }}
      search={{ from: "trades" }}
      className={className}
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
    >
      {children}
    </Link>
  );
}

function TradePreviewBody({ tradeId, onClose }: { tradeId: string; onClose: () => void }) {
  const { data: trade, isLoading, isError } = useTrade(tradeId);
  const { data: instrumentsData } = useInstruments();
  const { data: accountsData } = useAccounts();

  const symbol = useMemo(() => {
    if (trade === undefined) return undefined;
    const instrumentId = trade.legs[0]?.instrument_id;
    return instrumentsData?.items.find((i) => i.instrument_id === instrumentId)?.symbol;
  }, [trade, instrumentsData]);

  const accountName = useMemo(() => {
    if (trade === undefined) return undefined;
    return accountsData?.items.find((a) => a.account_id === trade.account_id)?.name;
  }, [trade, accountsData]);

  return (
    <>
      <DialogHeader className="border-b border-outline-variant px-5 py-4 text-left">
        <DialogTitle className="flex flex-wrap items-center gap-2 font-display text-xl">
          {symbol !== undefined ? <SymbolAvatar symbol={symbol} size="sm" /> : null}
          <span>{symbol ?? "Trade preview"}</span>
          {trade !== undefined ? (
            <>
              <DirectionBadge direction={trade.legs[0]?.direction} />
              <StatusBadge status={trade.status} />
              <OutcomeBadge status={trade.status} realizedPnl={trade.realized_pnl_amount} />
            </>
          ) : null}
        </DialogTitle>
        <DialogDescription className="text-xs text-on-surface-muted">
          {trade !== undefined ? subtitleLine(trade, accountName) : "Loading trade…"}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 overflow-y-auto px-5 py-4">
        {isLoading ? (
          <p className="text-on-surface-variant" role="status">
            Loading trade…
          </p>
        ) : isError || trade === undefined ? (
          <p className="text-danger" role="alert">
            The trade could not be loaded.
          </p>
        ) : (
          <PreviewContent trade={trade} />
        )}
      </div>

      <DialogFooter className="border-t border-outline-variant px-5 py-3">
        <Button asChild>
          <Link
            to="/trades/$tradeId"
            params={{ tradeId }}
            search={{ from: "trades" }}
            onClick={onClose}
          >
            Go to trade details
          </Link>
        </Button>
      </DialogFooter>
    </>
  );
}

// One-line header subtitle: trade day, account, and a compact hold time —
// matching the mockup's "Jul 25, 2025 · Main brokerage · held 27s".
function subtitleLine(trade: TradeResponse, accountName: string | undefined): string {
  const day = trade.opened_at ?? trade.closed_at;
  const parts: string[] = [];
  if (day !== null) parts.push(formatDate(day));
  if (accountName !== undefined) parts.push(accountName);
  if (trade.hold_duration_seconds !== null && trade.hold_duration_seconds !== undefined) {
    parts.push(`held ${formatHoldDuration(trade.hold_duration_seconds)}`);
  }
  return parts.length === 0 ? "Not yet opened" : parts.join(" · ");
}

// The compact stacked body from the "Trade panel compact" mockup: a tinted
// Net-P&L hero, an entry -> exit price flow, the Timing / Sizing fact sections,
// and a chronological executions strip. No editor affordances live here.
function PreviewContent({ trade }: { trade: TradeResponse }) {
  const { currency } = trade;
  const pnlClass = pnlClassName(trade.realized_pnl_amount);
  const pnlNumeric = Number(trade.realized_pnl_amount);
  const heroTint =
    pnlNumeric > 0
      ? "bg-success-soft"
      : pnlNumeric < 0
        ? "bg-danger-soft"
        : "bg-surface-container-low";

  // Chronological fills across every leg, plus the peak absolute net position
  // (the shares actually at risk in one direction) for the per-share edge.
  const { fills, size } = useMemo(() => {
    const flattened = trade.legs
      .flatMap((leg) => leg.fills)
      .slice()
      .sort(
        (a, b) => new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime(),
      );
    let running = "0";
    let peak = 0;
    for (const fill of flattened) {
      // Exact running net position; Number() only for the peak magnitude, which
      // is the per-share divisor (no equality test relies on it).
      running = sumDecimals([
        running,
        fill.action.startsWith("b") ? fill.quantity : `-${fill.quantity}`,
      ]);
      peak = Math.max(peak, Math.abs(Number(running)));
    }
    return { fills: flattened, size: peak };
  }, [trade.legs]);

  const perShare = size > 0 ? pnlNumeric / size : 0;
  const perShareLabel = `${perShare > 0 ? "+" : ""}${formatPrice(perShare.toFixed(4), { currency })}/sh`;

  return (
    <>
      <div className={`rounded-control border border-outline-variant ${heroTint} px-4 py-3`}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-muted">
          Net P&amp;L
          <span className="font-medium normal-case tracking-normal"> · realized</span>
        </p>
        <p className={`mt-1 font-display text-3xl font-semibold tabular-nums ${pnlClass}`}>
          {formatSignedMoney(trade.realized_pnl_amount, { currency })}
        </p>
        <p className="mt-1 flex items-center gap-2 font-data text-xs">
          <span className={pnlClass || "text-on-surface-variant"}>
            {formatPercent(trade.realized_return_pct)} ROI
          </span>
          {size > 0 ? (
            <>
              <span className="text-on-surface-muted">·</span>
              <span className={pnlClass || "text-on-surface-variant"}>{perShareLabel}</span>
            </>
          ) : null}
        </p>
      </div>

      <FillFlow trade={trade} />

      <Section title="Timing">
        <KeyRow label="Opened" value={formatDateTime(trade.opened_at)} plain />
        <KeyRow label="Closed" value={formatDateTime(trade.closed_at)} plain />
        <KeyRow label="Hold time" value={formatHoldDuration(trade.hold_duration_seconds)} />
      </Section>

      <Section title="Sizing">
        <KeyRow label="Quantity" value={size > 0 ? `${formatQuantity(size.toString())} sh` : EM_DASH} />
        <KeyRow label="Position size" value={formatMoney(trade.opening_notional_amount, { currency })} />
        <KeyRow label="Fees" value={formatMoney(trade.fees_amount, { currency })} />
      </Section>

      <Section title={`Executions · ${fills.length} ${fills.length === 1 ? "fill" : "fills"}`}>
        {fills.length === 0 ? (
          <p className="py-1.5 text-[13px] text-on-surface-variant">No executions yet.</p>
        ) : (
          <div className="overflow-hidden rounded-control border border-outline-variant">
            {fills.map((fill) => (
              <ExecutionRow key={fill.fill_id} fill={fill} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Notes">
        <div className="space-y-2 py-1">
          {/* The shared single-note autosave editor (ADR 0036 D3). It is NOT
              autofocused: focusing the textarea would scroll the panel down to
              the notes section, but the panel must open at the top (D8). */}
          <TradeNoteEditor tradeId={trade.trade_id} />
          {trade.notes.trim().length > 0 ? (
            <p className="whitespace-pre-wrap text-[13px] text-on-surface">{trade.notes}</p>
          ) : null}
        </div>
      </Section>
    </>
  );
}

// Entry -> exit price flow: the average entry and exit anchored at either end
// with the directional per-share move on the connecting track. Only shown once
// both averages exist (a trade that has both opened and closed exposure).
function FillFlow({ trade }: { trade: TradeResponse }) {
  const entry = trade.average_entry_price;
  const exit = trade.average_exit_price;
  if (entry === null || entry === undefined || exit === null || exit === undefined) {
    return null;
  }

  const { currency } = trade;
  const entryNum = Number(entry);
  const exitNum = Number(exit);
  // Long profits when price rises, short when it falls -- orient the move so a
  // gain reads positive regardless of side.
  const rawDelta = exitNum - entryNum;
  const move = trade.legs[0]?.direction === "short" ? -rawDelta : rawDelta;
  const movePct = entryNum !== 0 ? (move / entryNum) * 100 : 0;
  const pillClass =
    move > 0
      ? "bg-success-soft text-success"
      : move < 0
        ? "bg-danger-soft text-danger"
        : "bg-surface-container-high text-on-surface-variant";
  const sign = move >= 0 ? "+" : "-";
  const moveLabel = `${sign}${formatPrice(Math.abs(move).toFixed(4), { currency })} · ${sign}${Math.abs(movePct).toFixed(1)}%`;

  return (
    <div className="flex items-center gap-2 rounded-control border border-outline-variant px-4 py-3">
      <FlowNode label="Entry" value={formatPrice(entry, { currency })} time={formatTime(trade.opened_at)} />
      {/* Entry -> exit connector: a centered move chip on a hairline track that
          terminates in an arrowhead pointing at the exit price. */}
      <div className="flex flex-1 items-center gap-1.5">
        <div className="h-px flex-1 bg-outline-variant" />
        <span
          className={`whitespace-nowrap rounded-full px-2 py-0.5 font-data text-[10px] font-semibold tabular-nums ${pillClass}`}
        >
          {moveLabel}
        </span>
        <div className="h-px flex-1 bg-outline-variant" />
        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-on-surface-muted" />
      </div>
      <FlowNode
        label="Exit"
        value={formatPrice(exit, { currency })}
        time={formatTime(trade.closed_at)}
        align="right"
      />
    </div>
  );
}

function FlowNode({
  label,
  value,
  time,
  align = "left",
}: {
  label: string;
  value: string;
  time: string;
  align?: "left" | "right";
}) {
  return (
    <div className={`flex flex-col gap-0.5 ${align === "right" ? "items-end text-right" : ""}`}>
      <span className="text-[9px] font-bold uppercase tracking-wider text-on-surface-muted">
        {label}
      </span>
      <span className="font-data text-base font-semibold tabular-nums text-on-surface">{value}</span>
      <span className="font-data text-[10px] text-on-surface-muted">{time}</span>
    </div>
  );
}

// A titled fact block: an uppercase rule heading over a stack of key/value rows.
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-0.5 border-b border-outline-variant pb-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-muted">
        {title}
      </div>
      {children}
    </div>
  );
}

function KeyRow({
  label,
  value,
  plain = false,
}: {
  label: string;
  value: ReactNode;
  plain?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 text-[13px]">
      <span className="text-on-surface-variant">{label}</span>
      <span className={`font-semibold text-on-surface ${plain ? "" : "font-data tabular-nums"}`}>
        {value}
      </span>
    </div>
  );
}

function ExecutionRow({ fill }: { fill: FillResponse }) {
  const buy = fill.action.startsWith("b");
  return (
    <div className="flex items-center gap-2 border-b border-outline-variant px-3 py-2 text-xs last:border-b-0">
      <span
        className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
          buy ? "bg-success-soft text-success" : "bg-danger-soft text-danger"
        }`}
      >
        {FILL_ACTION_EXECUTION_LABELS[fill.action]}
      </span>
      <span className="font-data tabular-nums text-on-surface">
        {formatQuantity(fill.quantity)}
      </span>
      <span className="text-on-surface-muted">@</span>
      <span className="font-data tabular-nums text-on-surface">
        {formatPrice(fill.price_amount, { currency: fill.price_currency })}
      </span>
      <span className="ml-auto font-data text-[10px] text-on-surface-muted">
        {formatTime(fill.executed_at)}
      </span>
    </div>
  );
}
